import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { getAdminApiUrl, ENV_COLORS } from '@/pages/Admin/shared';
import {
  ANIMATION_STYLE,
  useCountUp,
  AnimatedNumber,
  HeroStat,
  StatRow,
  SectionCard,
  CHART_PALETTE,
  TOOLTIP_STYLE,
  TOOLTIP_LABEL_STYLE,
  TOOLTIP_ITEM_STYLE,
  tooltipFormatter,
  EmptyChart,
  Icons,
  fmtInt,
  fmtNum,
  fmtRelative,
  dateToIsoStart,
  dateToIsoEnd,
} from '../shared';
import { DateRangeControl, defaultRange } from '@/components/shared';
import { useBusinessAnalytics } from '../api/useBusinessAnalytics';
import { aggregateRows, type TopEntry } from './aggregations';

// ── Scroll-reveal hook ───────────────────────────────────────────────────────
// Returns a ref + whether the element has ever entered the viewport. One-shot:
// once revealed, stays revealed so animation doesn't replay on re-scroll.
function useInViewOnce<T extends HTMLElement>(
  rootMargin = '0px 0px -10% 0px',
): [React.RefObject<T | null>, boolean] {
  const ref = useRef<T>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    if (inView) return;
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setInView(true);
            observer.disconnect();
          }
        }
      },
      { rootMargin, threshold: 0.1 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [inView, rootMargin]);
  return [ref, inView];
}

// ── Section divider with shimmer sweep ───────────────────────────────────────
function SectionDivider({ label, delay = 0 }: { label: string; delay?: number }) {
  return (
    <div
      className="ss-fade-in mt-10 mb-4 flex items-center gap-3"
      style={{ animationDelay: `${delay}ms` }}
    >
      <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-violet-300/80">
        {label}
      </span>
      <div className="flex-1 h-px ss-divider-sweep" />
    </div>
  );
}

// ── Circular rate ring with animated fill ────────────────────────────────────
interface RateRingProps {
  label: string;
  ratio: number | null; // 0..1, or null for "no data"
  accent?: 'violet' | 'emerald' | 'amber' | 'sky' | 'rose' | 'fuchsia';
  sub?: string | null;
  delayMs?: number;
}

const RING_ACCENT: Record<NonNullable<RateRingProps['accent']>, { stroke: string; glow: string; chip: string }> = {
  violet: { stroke: '#a78bfa', glow: 'shadow-violet-900/40', chip: 'text-violet-300' },
  emerald: { stroke: '#34d399', glow: 'shadow-emerald-900/40', chip: 'text-emerald-300' },
  amber: { stroke: '#fbbf24', glow: 'shadow-amber-900/40', chip: 'text-amber-300' },
  sky: { stroke: '#38bdf8', glow: 'shadow-sky-900/40', chip: 'text-sky-300' },
  rose: { stroke: '#fb7185', glow: 'shadow-rose-900/40', chip: 'text-rose-300' },
  fuchsia: { stroke: '#e879f9', glow: 'shadow-fuchsia-900/40', chip: 'text-fuchsia-300' },
};

function RateRing({ label, ratio, accent = 'violet', sub, delayMs = 0 }: RateRingProps) {
  const [ref, inView] = useInViewOnce<HTMLDivElement>();
  const target = ratio ?? 0;
  const animatedRatio = useCountUp(inView ? target : 0, 1200);
  const pct = Math.max(0, Math.min(1, animatedRatio));
  const radius = 44;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference * (1 - pct);
  const a = RING_ACCENT[accent];
  const showPct = ratio !== null ? `${(animatedRatio * 100).toFixed(1)}%` : '—';

  return (
    <div
      ref={ref}
      className={`ss-fade-up relative overflow-hidden bg-[#14142a] border border-gray-800/80 rounded-2xl p-5 transition-all duration-300 hover:scale-[1.02] hover:shadow-lg ${a.glow}`}
      style={{ animationDelay: `${delayMs}ms` }}
    >
      <div className="pointer-events-none absolute -top-10 -right-10 w-28 h-28 rounded-full blur-2xl opacity-30" style={{ backgroundColor: a.stroke }} />
      <div className="relative flex items-center gap-4">
        <svg width="112" height="112" viewBox="0 0 112 112" className="shrink-0">
          <defs>
            <linearGradient id={`rr-${label}`} x1="0" x2="1" y1="0" y2="1">
              <stop offset="0%" stopColor={a.stroke} stopOpacity="1" />
              <stop offset="100%" stopColor={a.stroke} stopOpacity="0.6" />
            </linearGradient>
          </defs>
          <circle cx="56" cy="56" r={radius} stroke="#1f1f3a" strokeWidth="10" fill="none" />
          <circle
            cx="56"
            cy="56"
            r={radius}
            stroke={`url(#rr-${label})`}
            strokeWidth="10"
            fill="none"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={dashOffset}
            transform="rotate(-90 56 56)"
            style={{ filter: `drop-shadow(0 0 6px ${a.stroke}60)` }}
          />
          <text
            x="56"
            y="60"
            textAnchor="middle"
            className="fill-white font-bold tabular-nums"
            style={{ fontSize: 20 }}
          >
            {showPct}
          </text>
        </svg>
        <div className="flex-1 min-w-0">
          <p className={`text-[11px] font-semibold uppercase tracking-wider ${a.chip}`}>{label}</p>
          {sub && <p className="mt-1.5 text-xs text-gray-500 leading-snug">{sub}</p>}
        </div>
      </div>
    </div>
  );
}

// ── Top Performers list ──────────────────────────────────────────────────────
interface TopListProps {
  title: string;
  entries: TopEntry[];
  icon: React.ReactNode;
  accent: 'violet' | 'emerald' | 'amber' | 'sky' | 'rose' | 'fuchsia';
  delayMs?: number;
  valueFormat?: (n: number) => string;
}

function TopList({ title, entries, icon, accent, delayMs = 0, valueFormat = fmtInt }: TopListProps) {
  const [ref, inView] = useInViewOnce<HTMLDivElement>();
  const a = RING_ACCENT[accent];
  const topValue = entries[0]?.value ?? 1;

  return (
    <div ref={ref}>
      <SectionCard
        title={title}
        description={`Top ${Math.min(10, entries.length)} businesses`}
        icon={icon}
        delayMs={delayMs}
      >
        {entries.length === 0 && (
          <EmptyChart message="No data yet" height={120} />
        )}
        {entries.length > 0 && (
          <ol className="flex flex-col gap-2 list-none m-0 p-0">
            {entries.map((entry, i) => {
              const pct = Math.max(4, (entry.value / topValue) * 100);
              const width = inView ? `${pct}%` : '0%';
              return (
                <li
                  key={entry.business_id}
                  className="group relative"
                  style={{ transitionDelay: `${i * 50}ms` }}
                >
                  <Link
                    to={`/b2b/business-analytics/${entry.business_id}`}
                    className="no-underline block"
                  >
                    <div className="flex items-center gap-3">
                      <span
                        className={`w-6 h-6 rounded-md bg-black/40 border border-gray-800 flex items-center justify-center text-[11px] font-bold tabular-nums ${a.chip}`}
                      >
                        {i + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline justify-between gap-3 mb-1.5">
                          <span className="text-sm text-gray-200 group-hover:text-white transition-colors truncate">
                            {entry.business_name}
                          </span>
                          <span className="text-sm font-semibold text-white tabular-nums whitespace-nowrap">
                            {valueFormat(entry.value)}
                          </span>
                        </div>
                        <div className="h-1.5 rounded-full bg-gray-900/80 overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all duration-[1100ms] ease-out"
                            style={{
                              width,
                              background: `linear-gradient(90deg, ${a.stroke}, ${a.stroke}90)`,
                              transitionDelay: `${i * 60}ms`,
                              boxShadow: `0 0 8px ${a.stroke}40`,
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </Link>
                </li>
              );
            })}
          </ol>
        )}
      </SectionCard>
    </div>
  );
}

// ── Loading skeleton ─────────────────────────────────────────────────────────
function LoadingSkeleton() {
  return (
    <>
      <div className="ss-skeleton h-44 rounded-2xl mb-6" />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="ss-skeleton h-32 rounded-2xl" />
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        {[0, 1].map((i) => (
          <div key={i} className="ss-skeleton h-72 rounded-2xl" />
        ))}
      </div>
    </>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────
export default function FullReportPage(): React.ReactElement {
  const apiUrl = getAdminApiUrl('prod');

  const [[fromDate, toDate], setRange] = useState<[string, string]>(defaultRange);

  const queryParams = useMemo(() => {
    const params: { since?: string; until?: string } = {};
    if (fromDate) params.since = dateToIsoStart(fromDate);
    if (toDate) params.until = dateToIsoEnd(toDate);
    return params;
  }, [fromDate, toDate]);

  const { data, loading, error, refetch } = useBusinessAnalytics(apiUrl, queryParams);
  const stats = useMemo(() => aggregateRows(data.businesses), [data.businesses]);

  const dateLabel = useMemo(() => {
    if (!fromDate && !toDate) return 'All time';
    if (fromDate && toDate && fromDate === toDate) return fromDate;
    if (fromDate && toDate) return `${fromDate} → ${toDate}`;
    if (fromDate) return `From ${fromDate}`;
    return `Until ${toDate}`;
  }, [fromDate, toDate]);

  // Parallax: shift the hero blob a few pixels as the user scrolls. Cheap,
  // no library, rAF-throttled via scroll event.
  const [scrollY, setScrollY] = useState(0);
  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        setScrollY(window.scrollY);
        ticking = false;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  const blobShift = Math.min(40, scrollY * 0.08);

  const totals = stats.totals;
  const hasData = stats.business_count > 0;

  // Chart data prep ────────────────────────────────────────────────────────
  const engagementMixData = [
    { name: 'Check-ins', value: totals.check_ins_total },
    { name: 'Live sessions', value: totals.live_check_ins_started },
    { name: 'Favorites', value: totals.favorites_total },
    { name: 'Going intents', value: totals.going_intents_submitted },
    { name: 'Deal views', value: totals.deal_views_total },
  ];
  const engagementMixTotal = engagementMixData.reduce((s, d) => s + d.value, 0);

  const funnelData = [
    { stage: 'Sent', count: totals.business_notification_deliveries_sent, fill: '#a78bfa' },
    { stage: 'Delivered', count: totals.business_notification_deliveries_delivered, fill: '#38bdf8' },
    { stage: 'Opened', count: totals.business_notification_deliveries_opened, fill: '#34d399' },
  ];
  const funnelTotal = funnelData.reduce((s, d) => s + d.count, 0);

  const dealSourceData = [
    { name: 'Business page', value: totals.deal_views_from_business_page },
    { name: 'Notification', value: totals.deal_views_from_notification },
  ];
  const dealSourceTotal = dealSourceData.reduce((s, d) => s + d.value, 0);

  const eventInterestsData = [
    { name: 'Going', value: totals.event_interests_going },
    { name: 'Interested', value: totals.event_interests_interested },
  ];
  const eventInterestsTotal = eventInterestsData.reduce((s, d) => s + d.value, 0);

  const coverActionsData = [
    { action: 'Set', count: totals.cover_history_set },
    { action: 'Updated', count: totals.cover_history_updated },
    { action: 'Cleared', count: totals.cover_history_cleared },
    { action: 'Expired', count: totals.cover_history_expired },
  ];
  const coverActionsTotal = coverActionsData.reduce((s, d) => s + d.count, 0);

  return (
    <div className="min-h-screen bg-[#0f0f1a]">
      <style>{ANIMATION_STYLE}</style>
      <div className="max-w-7xl mx-auto px-6 pt-8 pb-16">
        {/* Breadcrumb */}
        <div className="mb-6 flex items-center gap-2 text-sm">
          <Link to="/b2b" className="text-gray-500 hover:text-gray-300 transition-colors no-underline">
            B2B Dashboards
          </Link>
          <span className="text-gray-700">/</span>
          <Link to="/b2b/business-analytics" className="text-gray-500 hover:text-gray-300 transition-colors no-underline">
            Business Insights
          </Link>
          <span className="text-gray-700">/</span>
          <span className="text-gray-300">Full Report</span>
        </div>

        {/* Error banner */}
        {error && (
          <div className="mb-6 bg-red-950/30 border border-red-800/40 rounded-xl p-4 flex items-center justify-between">
            <p className="text-sm text-red-400">{error}</p>
            <button
              onClick={refetch}
              className="px-3 py-1.5 text-sm font-medium text-red-300 bg-red-900/40 rounded-lg hover:bg-red-900/60 transition-colors cursor-pointer"
            >
              Retry
            </button>
          </div>
        )}

        {/* Hero banner */}
        <div className="ss-fade-up mb-6 bg-gradient-to-br from-violet-950/50 via-[#181831] to-fuchsia-950/30 border border-violet-800/40 rounded-3xl p-8 lg:p-10 shadow-2xl shadow-black/40 relative overflow-hidden">
          <div
            className="pointer-events-none absolute -top-32 -right-32 w-[28rem] h-[28rem] rounded-full bg-violet-600/20 blur-3xl ss-shimmer"
            style={{ transform: `translate3d(${-blobShift}px, ${blobShift}px, 0)` }}
          />
          <div
            className="pointer-events-none absolute -bottom-40 -left-24 w-[24rem] h-[24rem] rounded-full bg-fuchsia-600/10 blur-3xl"
            style={{ transform: `translate3d(${blobShift}px, ${-blobShift}px, 0)` }}
          />
          <div className="relative flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-xs font-semibold tracking-wide ${ENV_COLORS.prod.badge}`}>
                  <span className="w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse" />
                  PRODUCTION
                </span>
                <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-lg border bg-violet-950/40 border-violet-800/40 text-violet-300">
                  <span className="w-3.5 h-3.5 inline-block">{Icons.sparkles}</span>
                  Full Report
                </span>
              </div>
              <h1 className="text-4xl lg:text-6xl font-bold text-white font-display leading-[1.05] tracking-tight">
                SocialSpark<span className="text-violet-400">.</span>
                <br />
                <span className="text-gray-400 text-2xl lg:text-3xl font-normal tracking-normal">
                  {fromDate || toDate ? `Stats for ${dateLabel.toLowerCase()}` : 'All-time platform stats'}
                </span>
              </h1>
              <p className="mt-4 text-sm text-gray-500 max-w-xl">
                Aggregate performance across every production business, every check-in, every notification — rolled up into one picture.
              </p>
            </div>
            <div className="flex flex-col items-start lg:items-end gap-1 shrink-0">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">Businesses</span>
              <span className="text-5xl font-bold text-white tabular-nums">
                <AnimatedNumber value={stats.business_count} />
              </span>
              <span className="text-xs text-violet-300/80 mt-1">
                {loading ? 'Updating…' : `Last updated ${new Date().toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}`}
              </span>
            </div>
          </div>
        </div>

        {/* Date filter row */}
        <div className="mb-6 flex flex-col lg:flex-row lg:items-center gap-3 px-4 py-3 bg-[#181831] border border-gray-800/80 rounded-xl">
          <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">Date Range</span>
          <DateRangeControl
            from={fromDate || null}
            to={toDate || null}
            onChange={(f, t) => setRange([f ?? '', t ?? ''])}
          />
          <span className="text-xs text-violet-300/70 lg:ml-2">
            {loading && hasData && (
              <span className="inline-flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-violet-400 animate-pulse" />
                Updating…
              </span>
            )}
          </span>
        </div>

        {/* Body — loading, empty, or content */}
        {loading && !hasData && <LoadingSkeleton />}

        {!loading && !error && !hasData && (
          <div className="bg-[#1a1a2e] border border-gray-800 rounded-2xl p-12 text-center">
            <p className="text-gray-300 text-base font-semibold">No businesses match this range</p>
            <p className="text-gray-500 text-sm mt-1">Try expanding to all-time to see the full platform roll-up.</p>
            <button
              onClick={() => setRange(['', ''])}
              className="inline-block mt-4 px-4 py-2 text-sm font-medium text-violet-300 bg-violet-900/30 border border-violet-700/50 rounded-lg hover:bg-violet-900/50 transition-colors cursor-pointer"
            >
              Expand to all-time
            </button>
          </div>
        )}

        {hasData && (
          <>
            {/* Platform Pulse — 4 oversized hero tiles */}
            <SectionDivider label="Platform Pulse" delay={80} />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-2">
              <HeroStat
                label="Total Check-ins"
                value={totals.check_ins_total}
                sub={fmtRelative(stats.latest.last_check_in_at)
                  ? `Last: ${fmtRelative(stats.latest.last_check_in_at)}`
                  : 'No check-ins yet'}
                accent="violet"
                icon={Icons.pin}
                size="lg"
                delayMs={100}
              />
              <HeroStat
                label="Live Sessions Started"
                value={totals.live_check_ins_started}
                sub={`${fmtInt(totals.live_check_ins_active_now)} active right now`}
                accent="emerald"
                icon={Icons.bolt}
                size="lg"
                delayMs={180}
              />
              <HeroStat
                label="Total Favorites"
                value={totals.favorites_total}
                sub={`${fmtNum(stats.perBusinessAverages.favorites_per_business)} / business`}
                accent="amber"
                icon={Icons.heart}
                size="lg"
                delayMs={260}
              />
              <HeroStat
                label="Notifications Delivered"
                value={totals.business_notification_deliveries_delivered}
                sub={`${fmtInt(totals.business_notification_deliveries_sent)} attempted`}
                accent="sky"
                icon={Icons.bell}
                size="lg"
                delayMs={340}
              />
            </div>

            {/* Community & Engagement */}
            <SectionDivider label="Community & Engagement" delay={100} />
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <SectionCard
                title="Engagement Mix"
                description="Where attention went across the platform"
                icon={Icons.chart}
                className="lg:col-span-2"
                delayMs={120}
              >
                {engagementMixTotal === 0 ? (
                  <EmptyChart message="No engagement signals yet" height={260} />
                ) : (
                  <div className="ss-chart">
                    <ResponsiveContainer width="100%" height={260}>
                      <BarChart data={engagementMixData} layout="vertical" margin={{ top: 10, right: 24, left: 10, bottom: 0 }}>
                        <CartesianGrid stroke="#1f1f3a" strokeDasharray="3 3" horizontal={false} />
                        <XAxis type="number" stroke="#52527a" tick={{ fontSize: 11, fill: '#7e7ea8' }} />
                        <YAxis type="category" dataKey="name" stroke="#52527a" tick={{ fontSize: 12, fill: '#c4c4e0' }} width={110} />
                        <Tooltip
                          cursor={{ fill: '#1f1f3a55' }}
                          contentStyle={TOOLTIP_STYLE}
                          labelStyle={TOOLTIP_LABEL_STYLE}
                          itemStyle={TOOLTIP_ITEM_STYLE}
                          formatter={tooltipFormatter}
                        />
                        <Bar dataKey="value" radius={[0, 6, 6, 0]} animationDuration={1200} animationEasing="ease-out">
                          {engagementMixData.map((_, i) => (
                            <Cell key={i} fill={CHART_PALETTE[i % CHART_PALETTE.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </SectionCard>

              <SectionCard
                title="Cross-venue People"
                description="Per-business distinct-user sums"
                icon={Icons.users}
                delayMs={180}
              >
                <StatRow
                  label="Check-in users"
                  value={fmtInt(stats.distinctSums.check_ins_distinct_users)}
                  hint="Sum across venues. A user at multiple venues counts once per venue."
                />
                <StatRow
                  label="Live users"
                  value={fmtInt(stats.distinctSums.live_check_in_distinct_users)}
                />
                <StatRow
                  label="Deal-view users"
                  value={fmtInt(stats.distinctSums.deal_views_distinct_users)}
                />
                <StatRow
                  label="Going intents"
                  value={fmtInt(totals.going_intents_submitted)}
                />
              </SectionCard>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
              <HeroStat
                label="Avg Check-ins / Biz"
                value={stats.perBusinessAverages.check_ins_per_business}
                sub="Platform-wide average"
                accent="violet"
                icon={Icons.pin}
                format={(n) => Math.round(n).toLocaleString()}
                delayMs={120}
              />
              <HeroStat
                label="Avg Live Minutes"
                value={stats.weightedAverages.live_check_in_avg_minutes}
                sub="Weighted by completed sessions"
                accent="emerald"
                icon={Icons.bolt}
                format={(n) => n.toFixed(1)}
                delayMs={180}
              />
              <HeroStat
                label="Peak Live Minutes"
                value={stats.maxes.live_check_in_max_minutes}
                sub="Longest single session ever"
                accent="fuchsia"
                icon={Icons.fire}
                delayMs={240}
              />
              <HeroStat
                label="Live Completion Rate"
                value={stats.rates.live_completion_rate === null
                  ? null
                  : stats.rates.live_completion_rate * 100}
                sub="Completed ÷ started"
                accent="sky"
                icon={Icons.shield}
                format={(n) => `${n.toFixed(1)}%`}
                delayMs={300}
              />
            </div>

            {/* Notification Performance */}
            <SectionDivider label="Notification Performance" delay={80} />
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <SectionCard
                title="Delivery Funnel"
                description="Sent → delivered → opened"
                icon={Icons.bell}
                className="lg:col-span-2"
                delayMs={100}
              >
                {funnelTotal === 0 ? (
                  <EmptyChart message="No notifications sent yet" height={240} />
                ) : (
                  <div className="ss-chart">
                    <ResponsiveContainer width="100%" height={240}>
                      <BarChart data={funnelData} layout="vertical" margin={{ top: 10, right: 24, left: 10, bottom: 0 }}>
                        <CartesianGrid stroke="#1f1f3a" strokeDasharray="3 3" horizontal={false} />
                        <XAxis type="number" stroke="#52527a" tick={{ fontSize: 11, fill: '#7e7ea8' }} />
                        <YAxis type="category" dataKey="stage" stroke="#52527a" tick={{ fontSize: 12, fill: '#c4c4e0' }} width={90} />
                        <Tooltip
                          cursor={{ fill: '#1f1f3a55' }}
                          contentStyle={TOOLTIP_STYLE}
                          labelStyle={TOOLTIP_LABEL_STYLE}
                          itemStyle={TOOLTIP_ITEM_STYLE}
                          formatter={tooltipFormatter}
                        />
                        <Bar dataKey="count" radius={[0, 6, 6, 0]} animationDuration={1200} animationEasing="ease-out">
                          {funnelData.map((d, i) => (
                            <Cell key={i} fill={d.fill} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </SectionCard>

              <div className="flex flex-col gap-4">
                <RateRing
                  label="Delivery Rate"
                  ratio={stats.rates.notification_delivery_rate}
                  accent="sky"
                  sub="Delivered ÷ sent"
                  delayMs={160}
                />
                <RateRing
                  label="Open Rate"
                  ratio={stats.rates.notification_open_rate}
                  accent="emerald"
                  sub="Opened ÷ delivered"
                  delayMs={220}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
              <HeroStat
                label="Notifications Created"
                value={totals.business_notifications_created}
                accent="violet"
                icon={Icons.bell}
                delayMs={120}
              />
              <HeroStat
                label="Weekly Attributed Visits"
                value={totals.weekly_attributed_visits}
                sub="Check-ins traced to a tap"
                accent="amber"
                icon={Icons.pin}
                delayMs={180}
              />
              <HeroStat
                label="Weekly Sent"
                value={totals.weekly_notifications_sent}
                accent="sky"
                icon={Icons.trend}
                delayMs={240}
              />
              <HeroStat
                label="Weekly Opened"
                value={totals.weekly_notifications_opened}
                accent="emerald"
                icon={Icons.trend}
                delayMs={300}
              />
            </div>

            {/* Deals */}
            <SectionDivider label="Deals" delay={80} />
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <SectionCard
                title="Deal Views by Source"
                description="Where users discovered deals"
                icon={Icons.tag}
                delayMs={100}
              >
                {dealSourceTotal === 0 ? (
                  <EmptyChart message="No deal views in this period" height={220} />
                ) : (
                  <div className="ss-chart">
                    <ResponsiveContainer width="100%" height={220}>
                      <PieChart>
                        <Pie
                          data={dealSourceData}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          innerRadius={50}
                          outerRadius={80}
                          paddingAngle={2}
                          stroke="#0f0f1a"
                          animationDuration={1100}
                        >
                          {dealSourceData.map((_, i) => (
                            <Cell key={i} fill={['#a78bfa', '#38bdf8'][i]} />
                          ))}
                        </Pie>
                        <Tooltip
                          contentStyle={TOOLTIP_STYLE}
                          labelStyle={TOOLTIP_LABEL_STYLE}
                          itemStyle={TOOLTIP_ITEM_STYLE}
                          formatter={tooltipFormatter}
                        />
                        <Legend wrapperStyle={{ fontSize: 12, color: '#c4c4e0' }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </SectionCard>

              <SectionCard
                title="Deal Totals"
                description="Lifecycle state across all businesses"
                icon={Icons.tag}
                delayMs={160}
              >
                <StatRow label="Deals created" value={fmtInt(totals.deals_created)} />
                <StatRow label="Active now" value={fmtInt(totals.deals_active_now)} />
                <StatRow label="Scheduled" value={fmtInt(totals.deals_scheduled)} />
                <StatRow label="Expired" value={fmtInt(totals.deals_expired)} />
                <StatRow label="Deleted" value={fmtInt(totals.deals_deleted)} />
              </SectionCard>

              <RateRing
                label="Deal Tap-Through Rate"
                ratio={stats.rates.deal_tap_through_rate}
                accent="fuchsia"
                sub="Deal notifications opened ÷ sent"
                delayMs={220}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
              <HeroStat label="Total Deal Views" value={totals.deal_views_total} accent="violet" icon={Icons.tag} delayMs={120} />
              <HeroStat label="Views — From Page" value={totals.deal_views_from_business_page} accent="sky" icon={Icons.tag} delayMs={180} />
              <HeroStat label="Views — From Push" value={totals.deal_views_from_notification} accent="fuchsia" icon={Icons.bell} delayMs={240} />
              <HeroStat label="Deal Notifs Sent" value={totals.deal_notifications_sent} accent="amber" icon={Icons.bell} delayMs={300} />
            </div>

            {/* Events */}
            <SectionDivider label="Events" delay={80} />
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <SectionCard
                title="Going vs Interested"
                description="How users signal intent"
                icon={Icons.calendar}
                delayMs={100}
              >
                {eventInterestsTotal === 0 ? (
                  <EmptyChart message="No event interests in this period" height={220} />
                ) : (
                  <div className="ss-chart">
                    <ResponsiveContainer width="100%" height={220}>
                      <PieChart>
                        <Pie
                          data={eventInterestsData}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          innerRadius={50}
                          outerRadius={80}
                          paddingAngle={2}
                          stroke="#0f0f1a"
                          animationDuration={1100}
                        >
                          {eventInterestsData.map((_, i) => (
                            <Cell key={i} fill={['#34d399', '#fbbf24'][i]} />
                          ))}
                        </Pie>
                        <Tooltip
                          contentStyle={TOOLTIP_STYLE}
                          labelStyle={TOOLTIP_LABEL_STYLE}
                          itemStyle={TOOLTIP_ITEM_STYLE}
                          formatter={tooltipFormatter}
                        />
                        <Legend wrapperStyle={{ fontSize: 12, color: '#c4c4e0' }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </SectionCard>

              <SectionCard
                title="Event Totals"
                description="Lifecycle state across all hosts"
                icon={Icons.calendar}
                delayMs={160}
              >
                <StatRow label="Events created" value={fmtInt(totals.events_created)} />
                <StatRow label="Completed" value={fmtInt(totals.events_completed)} />
                <StatRow label="Cancelled" value={fmtInt(totals.events_cancelled)} />
                <StatRow label="Recurring" value={fmtInt(totals.events_recurring)} />
                <StatRow label="Interests removed" value={fmtInt(totals.event_interests_removed)} />
              </SectionCard>

              <RateRing
                label="Event Conversion"
                ratio={stats.rates.event_conversion_pct === null
                  ? null
                  : Math.min(1, stats.rates.event_conversion_pct / 100)}
                accent="emerald"
                sub={`${fmtInt(totals.event_actual_attendees_total)} actual of ${fmtInt(totals.event_planned_attendees_total)} planned`}
                delayMs={220}
              />
            </div>

            {/* Cover Charge */}
            <SectionDivider label="Cover Charge" delay={80} />
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <SectionCard
                title="Cover Lifecycle"
                description="Set / updated / cleared / expired events"
                icon={Icons.dollar}
                className="lg:col-span-2"
                delayMs={100}
              >
                {coverActionsTotal === 0 ? (
                  <EmptyChart message="No cover history yet" height={240} />
                ) : (
                  <div className="ss-chart">
                    <ResponsiveContainer width="100%" height={240}>
                      <BarChart data={coverActionsData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                        <CartesianGrid stroke="#1f1f3a" strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="action" stroke="#52527a" tick={{ fontSize: 12, fill: '#c4c4e0' }} />
                        <YAxis stroke="#52527a" tick={{ fontSize: 11, fill: '#7e7ea8' }} allowDecimals={false} />
                        <Tooltip
                          cursor={{ fill: '#1f1f3a55' }}
                          contentStyle={TOOLTIP_STYLE}
                          labelStyle={TOOLTIP_LABEL_STYLE}
                          itemStyle={TOOLTIP_ITEM_STYLE}
                          formatter={tooltipFormatter}
                        />
                        <Bar dataKey="count" radius={[6, 6, 0, 0]} animationDuration={1100}>
                          {coverActionsData.map((_, i) => (
                            <Cell key={i} fill={CHART_PALETTE[i % CHART_PALETTE.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </SectionCard>

              <div className="flex flex-col gap-4">
                <HeroStat
                  label="Live Right Now"
                  value={stats.coverActiveCount}
                  sub="Businesses charging cover"
                  accent="amber"
                  icon={Icons.fire}
                  delayMs={160}
                />
                <RateRing
                  label="Cover Notif Open Rate"
                  ratio={stats.rates.cover_notification_open_rate}
                  accent="amber"
                  sub="Cover notifications opened ÷ sent"
                  delayMs={220}
                />
              </div>
            </div>

            {/* Top Performers */}
            <SectionDivider label="Top Performers" delay={80} />
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              <TopList
                title="Most Check-ins"
                entries={stats.topPerformers.check_ins}
                icon={Icons.pin}
                accent="violet"
                delayMs={100}
              />
              <TopList
                title="Most Favorited"
                entries={stats.topPerformers.favorites}
                icon={Icons.heart}
                accent="amber"
                delayMs={180}
              />
              <TopList
                title="Most Notification Opens"
                entries={stats.topPerformers.notif_opens}
                icon={Icons.bell}
                accent="sky"
                delayMs={260}
              />
              <TopList
                title="Most Live Sessions"
                entries={stats.topPerformers.live_sessions}
                icon={Icons.bolt}
                accent="emerald"
                delayMs={340}
              />
              <TopList
                title="Most Deal Views"
                entries={stats.topPerformers.deal_views}
                icon={Icons.tag}
                accent="fuchsia"
                delayMs={420}
              />
            </div>

            {/* Footer */}
            <div className="mt-10 text-center space-y-1">
              <p className="text-xs text-gray-500">
                Aggregated from {fmtInt(stats.business_count)} production businesses · {dateLabel.toLowerCase()}
              </p>
              <p className="text-[11px] text-gray-700">
                Generated {new Date().toLocaleString()}
              </p>
              <button
                onClick={refetch}
                disabled={loading}
                className="mt-4 px-4 py-2 text-sm font-medium text-violet-300 bg-violet-900/30 border border-violet-700/50 rounded-lg hover:bg-violet-900/50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
              >
                {loading ? 'Refreshing…' : 'Refresh data'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
